import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

/**
 * Styled via the static `.error-boundary-*` classes in public/index.html — NOT
 * theme tokens, NOT styled-components — because the ThemeProvider itself may be
 * broken (or already unmounted, taking its injected style tags with it) when
 * this boundary renders. Those classes mirror OS `prefers-color-scheme`
 * directly so this fallback still isn't a white-on-white (or black-on-black)
 * dead end in dark mode.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary-container" role="alert">
          <div aria-hidden="true" className="error-boundary-icon">
            &#9888;
          </div>
          <h1 className="error-boundary-title">Something went wrong</h1>
          <p className="error-boundary-message">
            An unexpected error occurred. Please try reloading the page.
          </p>
          <button className="error-boundary-button" type="button" onClick={this.handleReload}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
